import { reduce, sortBy, prop, filter, last, cond, equals, always } from 'ramda';
import { capitalize } from '@peloton/text/titleize';
import { toDiffDuration } from '@peloton/time/time';
import type {
  ChallengeStatus,
  Challenge,
  ChallengeSummary,
  ChallengeProgress,
  ChallengeDetail,
  ChallengeTier,
} from '@engage/challenges/models';
import { ChallengeParticipationType } from '@engage/challenges/models';

export enum Source {
  TopNavigation = 'Top Navigation',
  Tooltip = 'Tooltip',
  Home = 'Home',
}

export enum AnalyticsChallengeType {
  Community = 'Community',
  Personal = 'Personal',
  Traditional = 'Traditional',
}

export enum CreateChallengeStep {
  ChallengeType = 'Challenge Type',
  ChallengeFitnessDiscipline = 'Challenge Fitness Discipline',
  ChallengeDuration = 'Challenge Duration',
  ChallengeInputValue = 'Challenge Input Value',
  ChallengeStartDate = 'Challenge Start Date',
}

const toStatus = (status: ChallengeStatus) => capitalize(status as string);

export const trackViewedChallengesScreen = (
  status: ChallengeStatus,
  challenges: Challenge[],
  source: Source,
) => {
  const state = toStatus(status);

  const countKey = `[${state} Challenges Count]`;
  const joinedKey = `[${state} Challenges Joined]`;
  const joined = reduce(
    (sum, challenge) => (challenge.progress.hasJoined ? sum + 1 : sum),
    0,
    challenges,
  );

  return {
    event: `Viewed ${state} Challenges Screen`,
    [countKey]: challenges.length,
    [joinedKey]: joined,
    Source: source as string,
  };
};

export const trackViewedCompletedChallengesScreen = (
  challenges: Challenge[],
  source: Source,
) => ({
  event: 'Viewed Past Challenges Screen',
  '[Past Challenges]': challenges.length,
  Source: source as string,
});

export const trackViewedPersonalChallengeUI = (
  stepNumber: number,
  stepName: CreateChallengeStep,
) => ({
  event: 'Viewed Create Personal Challenge UI',
  '[Step Number]': stepNumber,
  '[Step Name]': stepName,
});

export const trackPublishPersonalChallenge = () => ({
  event: 'Tapped Publish Personal Challenge',
});

export const trackClickedCreateChallenge = () => ({
  event: 'Create Personal Challenge',
});

export const trackDisplayedChallengesTooltip = () => ({
  event: 'Displayed Challenges Tooltip',
});

export const trackViewedInAppAnnouncement = () => ({
  event: 'Viewed In-App Announcement',
  '[Message Name]': 'Challenges on Web',
});

export const trackViewedFriendsInChallenge = (
  challengeSummary: ChallengeSummary,
  numberOfFriends: number,
) => ({
  event: 'Viewed Friends in Challenge',
  '[Challenge ID]': challengeSummary.id,
  '[Challenge Name]': challengeSummary.title,
  '[Number of Friends]': numberOfFriends,
  '[Challenge Status]': toStatus(challengeSummary.status),
});

// Helper to return a percentage value (from `0` to `100`) given a current value and
// the value required to achieve the highest (most difficult) tier in a challenge.
// If there is no highest tier, which could happen if a Challenge does not
// have any tiers at all, we consider the progress to be 100%.
export const calculatePercentageProgress = (
  currentMetricValue: number,
  highestTierMetricValue: number = 100,
) => Math.min(Math.round((currentMetricValue / highestTierMetricValue) * 100), 100);

export const trackViewedChallengeDetail = (
  challengeSummary: ChallengeSummary,
  challengeDetail: ChallengeDetail,
  progress: ChallengeProgress,
  numberOfParticipants: number,
  numberOfFriends: number,
  source?: string,
) => {
  const status = toStatus(challengeSummary.status);

  const tiers = sortBy<ChallengeTier>(
    prop('metricValue'),
    filter(prop('hasReward'), challengeDetail.tiers),
  );
  const mostDifficult = last(tiers);
  const percentageProgress = calculatePercentageProgress(
    progress.metricValue,
    mostDifficult?.metricValue,
  );
  return {
    event: 'Viewed Challenge Detail',
    '[Challenge ID]': challengeSummary.id,
    '[Challenge Name]': challengeSummary.title,
    '[Challenge Status]': toStatus(challengeSummary.status),
    '[Challenge Source]': mapChallengeTypeToAnalytics(challengeSummary.challengeType),
    '[Challenge Fitness Disciplines]': challengeSummary?.fitnessDisciplines ?? '',
    '[Challenge Duration]': toDiffDuration(
      challengeSummary.startTime,
      challengeSummary.endTime,
    ).humanize(),
    '[Challenge Input Value]': Number(
      challengeSummary.description.match(/\d+/i)?.[0] ?? 0,
    ),
    '[Challenge Start Date]': challengeSummary.startTime.format('YYYY-MM-DD'),
    '[Challenge Audience]': challengeSummary.challengeAudienceType
      ? capitalize(challengeSummary.challengeAudienceType)
      : undefined,
    '[Company Name]': challengeSummary.challengeAudienceName,
    '[Current Tier]': progress.currentTier?.title,
    '[Next Tier]': progress.nextTier?.title,
    '[Percentage Progress]': percentageProgress,
    Source: source ? source : `${status} Challenges`,
    '[Has Joined]': progress.hasJoined,
    '[Number of Friends]': numberOfFriends,
    '[Number of Participants]': numberOfParticipants,
  };
};

export const mapChallengeTypeToAnalytics = (
  challengeType: ChallengeParticipationType,
): AnalyticsChallengeType =>
  cond([
    [
      equals(ChallengeParticipationType.Community),
      always(AnalyticsChallengeType.Community),
    ],
    [
      equals(ChallengeParticipationType.Individual),
      always(AnalyticsChallengeType.Traditional),
    ],
    [
      equals(ChallengeParticipationType.Personal),
      always(AnalyticsChallengeType.Personal),
    ],
  ])(challengeType);

export const trackEarnedTier = (
  challengeSummary: ChallengeSummary,
  progress: ChallengeProgress,
) => {
  const percentageProgress = calculatePercentageProgress(
    progress.metricValue,
    progress.nextTier?.metricValue,
  );
  return {
    event: 'Earned Tier',
    '[Challenge ID]': challengeSummary.id,
    '[Earned Tier]': progress.currentTier?.title,
    Title: challengeSummary.title,
    '[Progress Percentage]': percentageProgress,
    '[Challenge Source]': mapChallengeTypeToAnalytics(challengeSummary.challengeType),
    '[Challenge Fitness Disciplines]': challengeSummary?.fitnessDisciplines ?? '',
    '[Challenge Duration]': toDiffDuration(
      challengeSummary.startTime,
      challengeSummary.endTime,
    ).humanize(),
    '[Challenge Input Value]': Number(
      challengeSummary.description.match(/\d+/i)?.[0] ?? 0,
    ),
    '[Challenge Start Date]': challengeSummary.startTime.format('YYYY-MM-DD'),
  };
};

export const trackJoinedChallenge = (
  challengeSummary: ChallengeSummary,
  progress: ChallengeProgress,
  numberOfFriends: number,
) => {
  const percentageProgress = calculatePercentageProgress(
    progress.metricValue,
    progress.nextTier?.metricValue,
  );
  return {
    event: 'Joined Challenge',
    '[Challenge ID]': challengeSummary.id,
    '[Current Tier]': progress.currentTier?.title,
    '[Next Tier]': progress.nextTier?.title,
    '[Progress Percentage]': percentageProgress,
    Status: toStatus(challengeSummary.status),
    '[Number of Friends]': numberOfFriends,
    '[Challenge Source]': mapChallengeTypeToAnalytics(challengeSummary.challengeType),
    '[Challenge Fitness Disciplines]': challengeSummary?.fitnessDisciplines ?? '',
    '[Challenge Duration]': toDiffDuration(
      challengeSummary.startTime,
      challengeSummary.endTime,
    ).humanize(),
    '[Challenge Input Value]': Number(
      challengeSummary.description.match(/\d+/i)?.[0] ?? 0,
    ),
    '[Challenge Start Date]': challengeSummary.startTime.format('YYYY-MM-DD'),
  };
};

export const trackLeftChallenge = (
  challengeSummary: ChallengeSummary,
  progress: ChallengeProgress,
  numberOfFriends: number,
) => {
  const percentageProgress = calculatePercentageProgress(
    progress.metricValue,
    progress.nextTier?.metricValue,
  );
  return {
    event: 'Left Challenge',
    '[Challenge ID]': challengeSummary.id,
    '[Current Tier]': progress.currentTier?.title,
    '[Next Tier]': progress.nextTier?.title,
    '[Progress Percentage]': percentageProgress,
    Status: toStatus(challengeSummary.status),
    '[Number of Friends]': numberOfFriends,
    '[Challenge Source]': mapChallengeTypeToAnalytics(challengeSummary.challengeType),
    '[Challenge Fitness Disciplines]': challengeSummary?.fitnessDisciplines ?? '',
    '[Challenge Duration]': toDiffDuration(
      challengeSummary.startTime,
      challengeSummary.endTime,
    ).humanize(),
    '[Challenge Input Value]': Number(
      challengeSummary.description.match(/\d+/i)?.[0] ?? 0,
    ),
    '[Challenge Start Date]': challengeSummary.startTime.format('YYYY-MM-DD'),
  };
};
